import {TextSearchFilterComponent} from './text-search-filter/text-search-filter.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CacheDataService} from '../../api/services/cache-data.service';
import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, forwardRef, Input, NgZone, OnInit, ViewChild, Output, EventEmitter} from '@angular/core';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {Title} from '../../api/models/title';

@Component({
  selector: 'title-search',
  templateUrl: './text-search-filter/text-search-filter.component.html',
  styleUrls: ['./text-search-filter/text-search-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TitleSearchComponent),
      multi: true
    }
  ]
})
export class TitleSearchComponent extends TextSearchFilterComponent<Title> implements ControlValueAccessor, OnInit, AfterViewInit
{
  @ViewChild('scrollDiv') scrollDiv : ElementRef;

  @Input() fixedHeight?: number;
  @Input() requestNewSearch : (titleNames: string[], pageNumber: number, pageSize: number) => Promise<{totalPages: number, titles: Title[]}>;
  @Input() requestNextBatch : (titleNames: string[], pageNumber: number, pageSize: number) => Promise<{totalPages: number, titles: Title[]}>;

  @Input() showSelected: boolean = false;

  @Output() dataLoadComplete = new EventEmitter<void>();

  constructor(cacheDataService: CacheDataService, ngZone: NgZone) {
    super(cacheDataService, ngZone);
  }

  protected getHeightValue() : number | undefined {
    if (this.fixedHeight) {
      return this.fixedHeight;
    }
    return undefined;
  }

  protected getShowSelected(): boolean {
    return this.showSelected;
  }

  ngOnInit() {
    this.callNgOnInit();
  }

  ngAfterViewInit() {
    this.callNgAfterViewInit();
  }

  public dataUpdated(shouldSearch: boolean = false) {
    if (shouldSearch) {
      this.newSearch(true, false);
    }
  }

  protected getDivScroller(): ElementRef {
    return this.scrollDiv;
  }

  protected getIdentifier(target: Title): string {
    return target.key;
  }

  protected isSelected(target: Title) : boolean {
    return this.selectedKeys.has(this.getIdentifier(target));
  }

  protected getDisplay(target: Title): string {
    if (!target.name || target.name.length === 0) {
      return '<NONAME>';
    } else {
      return target.name;
    }
  }

  protected async newSearch(hasSearchChanges: boolean, hasSelectedChanges: boolean) {
    this.reloading = true;
    const currentSearch = ++this.searchCounter;

    if (hasSelectedChanges || this.selectedValuesPage === -1) {
      this.selectedValuesPage = 1;
      this.selectedValuesTotalPages = Math.ceil((this._value?.selectedValues?.length ?? 0) / 100);
      this.paginatedSelectedValues = this._value?.selectedValues?.slice(0, 100) ?? [];
    }

    if (this.selectedValuesPage >= this.selectedValuesTotalPages || this.selectedValuesTotalPages <= 1) {
      const result = await this.requestNewSearch(this._value?.searchTerms ?? [], 1, 100);

      if (currentSearch !== this.searchCounter) return;

      this.currentPage = 1;
      this.totalPages = result.totalPages;
      this.sourceValues = result.titles;

      this.reloading = false;
      this.dataLoadComplete.emit();
    } else {
      this.currentPage = 0;
      this.totalPages = 0;
      this.sourceValues = [];
      this.reloading = false;
    }
  }

  protected async getNextBatch() {
    if (this.currentPage < this.totalPages) {
      this.loading = true;
      const currentSearch = this.searchCounter;
      this.currentPage++;

      const result = await this.requestNextBatch(this._value?.searchTerms ?? [], this.currentPage, 100);

      if (currentSearch !== this.searchCounter) return;

      this.totalPages = result.totalPages;
      this.loading = false;
      this.sourceValues = [...this.sourceValues, ...result.titles];
      this.dataLoadComplete.emit();
    }
  }

  protected async getNextSelectedBatch() {
    if (this.selectedValuesPage < this.selectedValuesTotalPages) {
      this.loadingSelected = true;
      this.selectedValuesPage++;
      const nextBatch = this._value.selectedValues.slice((this.selectedValuesPage - 1) * 100, this.selectedValuesPage * 100);
      this.paginatedSelectedValues = [...this.paginatedSelectedValues, ...nextBatch];
      this.loadingSelected = false;

      if (this.selectedValuesPage === this.selectedValuesTotalPages) {
        this.loading = true;
        const currentSearch = this.searchCounter;
        const result = await this.requestNewSearch(this._value?.searchTerms ?? [], 1, 100);

        if (currentSearch !== this.searchCounter) return;

        this.currentPage = 1;
        this.totalPages = result.totalPages;
        this.sourceValues = result.titles;
        this.loading = false;
      }
    }
  }

  protected getSearchPlaceholder(): string {
    return 'Job Title Keyword...';
  }
}
